import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  StackProps,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { lazy, Suspense, useState } from "react";
import * as yup from "yup";
import { BankInformationInterface } from "../../constants/SupplierProfile";
import useCustomToast from "../../lib/useCustomToast";
import { getAxiosInstance } from "../../utils/helperFunction";
import { NumberInput } from "../input/NumberInput";
import { CardSkeleton } from "../skeleton/CardSkeleton";

const SelectPublicApi = lazy(() => import("../input/SelectPublicApi"));

interface Props extends StackProps {
  data: BankInformationInterface | undefined;
  pageType: number;
  loaded: boolean;
}

export const TabBankAccount = ({ data, loaded, pageType, ...rest }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();

  const initialValues = {
    bankName: data && data.bank_name,
    bankAccount: data && data.acc_number,
    bankAccountOwner: data && data.acc_name,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      bankName: yup.string().required("Nama Bank harus diisi"),
      bankAccount: yup
        .string()
        .required("Rekening Bank harus diisi")
        .matches(/^\d+$/, "Rekening Bank hanya boleh berisi angka"),
      bankAccountOwner: yup
        .string()
        .required("Nama pemilik rekening harus diisi"),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      const axiosInstance = getAxiosInstance(pageType);
      let userType: string = "buyer";

      if (pageType === 2) {
        userType = "reseller";
      } else if (pageType === 3) {
        userType = "supplier";
      }

      const request = {
        bank_name: values.bankName,
        acc_name: values.bankAccountOwner,
        acc_number: values.bankAccount,
      };

      axiosInstance
        .put(`/v1/${userType}/update-some-profile`, request)
        .then((res) => {
          toast({
            title: res.data.message,
            status: "success",
          });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <form
      id="bankInformationForm"
      onSubmit={formik.handleSubmit}
      style={{ width: "100%" }}
    >
      <VStack w={"100%"} gap={6} align={"stretch"} {...rest}>
        <FormControl
          isInvalid={
            formik.errors.bankName && formik.touched.bankName ? true : false
          }
        >
          <FormLabel htmlFor="bankName">Nama Bank</FormLabel>
          <Suspense fallback={<CardSkeleton row={1} height={40} />}>
            <SelectPublicApi
              endpoint="/v1/masterdata/bank"
              inputValue={formik.values.bankName}
              onSelected={(id) => {
                formik.setFieldValue("bankName", id);
              }}
            />
          </Suspense>
          <FormErrorMessage>{formik.errors.bankName}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.bankAccount && formik.touched.bankAccount
              ? true
              : false
          }
        >
          <FormLabel htmlFor="bankAccount">Rekening Bank</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <NumberInput
              name="bankAccount"
              inputValue={formik.values.bankAccount}
              isCurrency={false}
              onConfirm={(input) => {
                formik.setFieldValue("bankAccount", input);
              }}
            />
          )}
          <FormErrorMessage>{formik.errors.bankAccount}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.bankAccount && formik.touched.bankAccount
              ? true
              : false
          }
        >
          <FormLabel htmlFor="bankAccountOwner">
            Nama Pemilik Rekening
          </FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <Input
              name="bankAccountOwner"
              type="text"
              autoComplete="off"
              onChange={formik.handleChange}
              value={formik.values.bankAccountOwner || ""}
            />
          )}
          <FormErrorMessage>{formik.errors.bankAccountOwner}</FormErrorMessage>
        </FormControl>

        <Button
          form="bankInformationForm"
          type="submit"
          colorScheme="red"
          isLoading={loading}
          isDisabled={loaded ? false : true}
          loadingText={"Loading"}
          w={"fit-content"}
        >
          {!!data?.bank_name && !!data?.acc_name && !!data?.acc_number
            ? "Ubah Informasi Rekening"
            : "Lengkapi Informasi Rekening"}
        </Button>
      </VStack>
    </form>
  );
};
